import React, { useEffect, useState } from 'react';
import { 
  Briefcase, CheckCircle, Globe, Users, HardHat, Cpu, Factory, Ship, Building2, 
  ShieldCheck, ArrowRight, Settings, Heart, Laptop, Home, Database, 
  Lock,Trophy, Code, Cloud, Microscope, Construction, Truck, Anchor, Flame
} from 'lucide-react';
import SEO from '../seo/SEO';

const ManpowerRecruitmentPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans text-slate-900">
      <SEO
        title="Manpower & Recruitment Services | GlobalEPRC"
        description="GlobalEPRC provides ethical, reliable manpower and recruitment solutions across skilled, semi-skilled and unskilled roles for organizations across industries."
        path="/manpower-recruitment"
      />

      {/* 1. HERO SECTION - GENERAL MANPOWER / RECRUITMENT */}
      <section className="pt-48 pb-20 bg-slate-50 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-amber-50/50 -skew-x-12 translate-x-20"></div>
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="flex flex-col lg:flex-row gap-12 items-center">
            <div className="lg:w-3/5">
              <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-1.5 rounded-full mb-6 font-bold text-xs uppercase tracking-widest">
                <Users className="h-4 w-4" /> Global Talent Solutions
              </div>
              <h1 className="text-5xl md:text-5xl font-black text-slate-900 mb-6 leading-tight">
                Manpower /
                <span className="text-gold-gradient">Recruitment</span>
              </h1>
              <p className="text-xl text-slate-600 leading-relaxed max-w-2xl mb-6">
                We provide ethical and reliable manpower recruitment solutions to meet the workforce needs of organizations across industries. Our services cover skilled, semi-skilled, and unskilled manpower, ensuring the right talent is placed in the right role.
              </p>
              <p className="text-slate-600 leading-relaxed max-w-2xl">
                From sourcing and screening to selection and onboarding, we manage the complete recruitment process with a strong focus on quality, compliance, and timely delivery. With access to talent pools across India, South Asia, and Africa, we support both domestic and international hiring requirements.
              </p>
            </div>
            <div className="lg:w-2/5 grid grid-cols-1 gap-4">
              <img src="/rq.jpeg" alt="Recruitment" className="rounded-3xl shadow-xl mt-12" />
            </div>
          </div>
        </div>
      </section>

      {/* 2. VALUE PROPOSITION & ETHICS */}
      <section className="py-16 bg-white border-b border-slate-100">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="bg-slate-900 p-10 rounded-[2.5rem] text-white shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gold-gradient opacity-10 rounded-full -mr-16 -mt-16"></div>
              <h3 className="text-2xl font-bold mb-4 text-amber-400">Our Solutions Approach</h3>
              <p className="text-slate-300 leading-relaxed">
                Our recruitment solutions are designed to be cost-effective, scalable, and customized, helping clients build productive teams while candidates gain sustainable career opportunities. We adhere to international standards, transparent processes, and strict compliance to ensure both employers and job seekers experience a smooth and reliable recruitment journey.
              </p>
            </div>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="bg-amber-100 p-3 rounded-xl"><Globe className="text-amber-600 w-6 h-6" /></div>
                <div>
                  <h4 className="font-bold text-lg">International Standards</h4>
                  <p className="text-slate-500">Adhering to global compliance and transparent ethical hiring processes.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="bg-amber-100 p-3 rounded-xl"><ShieldCheck className="text-amber-600 w-6 h-6" /></div>
                <div>
                  <h4 className="font-bold text-lg">Strict Compliance</h4>
                  <p className="text-slate-500">Ensuring all legal and regional labor laws are strictly followed.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. WHITE-COLLAR SECTION */}
    <section className="py-20 bg-slate-50">
      <div className="container mx-auto px-4 md:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-6">
            <div>
              <h2 className="text-3xl font-black text-slate-900 mb-2">1. White-Collar Recruitment</h2>
              <p className="text-amber-600 font-bold text-xs uppercase tracking-widest">Placing People, Placing Confidence</p>
            </div>
            
            <p className="text-slate-600 leading-relaxed">
              We help organizations hire professionals across engineering, administration, finance, IT, healthcare, management, and technical fields.
            </p>

            {/* Shortened List with Accent Border */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 border-l-2 border-amber-400 pl-6 py-2">
              {[
                'Senior-level professionals', 
                'Mid-level managers', 
                'Entry-level executives', 
                'Technical specialists', 
                'Administrative staff'
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-sm font-bold text-slate-700">
                  <span className="h-1 w-1 rounded-full bg-amber-600"></span>
                  {item}
                </div>
              ))}
            </div>
          </div>

          {/* Right Image - Slightly smaller with clean border */}
          <div className="relative">
            <div className="absolute -inset-4 bg-amber-100 rounded-[2rem] -z-10 transform rotate-3"></div>
            <img 
              src="/white.jpeg" 
              alt="White Collar" 
              className="rounded-3xl border-2 border-amber-200 shadow-xl w-full object-contain h-64 lg:h-80" 
            />
          </div>
        </div>
      </div>
    </section>

      {/* 4. BLUE-COLLAR SECTION */}
  <section className="py-24 bg-white">
  <div className="container mx-auto px-4 md:px-8">
    <h2 className="text-4xl font-black mb-6">2. Blue-Collar Recruitment</h2>
    <p className="text-lg text-slate-600 mb-12 max-w-3xl">
      Our strong presence in key sourcing regions gives us access to thousands of skilled and semi-skilled workers across industries such as:
    </p>

    <div className="grid lg:grid-cols-2 gap-12 items-start">
      {/* Left Side: Role and Industry Lists */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Role Groups with Accent Border */}
        {/* <div className="flex flex-col border-l-2 border-amber-400 pl-6 py-2 space-y-4">
          {[
            "Engineers", 
            "BMS Operators", 
            "Mechanical Technicians", 
            "Industrial Electricians", 
            "HVAC Technicians", 
            "Pipe Fitters"
          ].map((role, i) => (
            <div key={i} className="flex items-center gap-3 text-slate-700 font-bold text-sm">
              <span className="h-1.5 w-1.5 rounded-full bg-slate-900"></span>
              {role}
            </div>
          ))}
        </div> */}

        {/* Combined Industry Grid */}
        <div className="grid grid-cols-1 gap-3">
          {[
            { title: "Oil & Gas", icon: Factory },
            { title: "Construction", icon: Building2 },
            { title: "Manufacturing", icon: Briefcase },
            { title: "Logistics", icon: Ship },
            { title: "Facility Management", icon: Home }
          ].map((industry, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100 group hover:bg-gold-gradient hover:text-white transition-all duration-300">
              <industry.icon className="h-5 w-5 text-amber-600 group-hover:text-white transition-colors" />
              <h5 className="font-bold text-slate-900 text-xs group-hover:text-white transition-colors uppercase tracking-tight">{industry.title}</h5>
            </div>
          ))}
        </div>
      </div>

      {/* Right Side: Industrial Image */}
      <div className="relative group">
        {/* Decorative element to match White-Collar section style */}
        <div className="absolute -inset-4 bg-slate-100 rounded-[2rem] -z-10 transform -rotate-2 group-hover:rotate-0 transition-transform duration-500"></div>
        <img 
          src="/blue.jpeg" 
          alt="Blue Collar Workforce" 
          className="rounded-3xl shadow-2xl w-full object-cover h-[400px]" 
        />
        {/* Industry Tag Overlay */}
        <div className="absolute bottom-6 left-6 bg-white/90 backdrop-blur-sm p-4 rounded-2xl shadow-lg border border-amber-100">
          <p className="text-amber-600 font-black text-[10px] uppercase tracking-[0.2em] mb-1">Global Presence</p>
          <p className="text-slate-900 font-bold text-sm">Skilled & Semi-Skilled Workforce</p>
        </div>
      </div>
    </div>
  </div>
</section>

      {/* 5. IT RECRUITMENT SOLUTIONS */}
{/* Updated Background to Deep Midnight Blue to contrast with standard Slate footers */}
{/* Background changed to a sophisticated Deep Navy/Charcoal Gradient */}
{/* Section transitioned to a clean white/slate-50 theme */}
{/* Section: White SaaS-Style IT Talent Solutions */}
<section className="py-24 bg-white relative overflow-hidden border-t border-slate-100">
  
  {/* Subtle technical grid pattern */}
  <div className="absolute top-0 left-0 w-full h-full opacity-[0.05] pointer-events-none" 
       style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h40v40H0V0zm1 1h38v38H1V1z' fill='%2364748b' fill-opacity='0.4' fill-rule='evenodd'/%3E%3C/svg%3E")` }}>
  </div>

  <div className="container mx-auto px-4 md:px-8 relative z-10">
    <div className="flex flex-col lg:flex-row gap-16 items-center mb-20">
      {/* Left side: Content */}
      <div className="lg:w-1/2">
        <div className="inline-block px-4 py-1.5 rounded-full bg-amber-50 border border-amber-100 text-amber-700 text-[10px] font-black uppercase tracking-[0.2em] mb-6">
          Future-Ready Talent
        </div>
        <h2 className="text-4xl md:text-5xl font-black mb-6 tracking-tight text-slate-900">
          Our Recruitment & <span className="text-gold-gradient">Talent Solutions (IT)</span>
        </h2>
        <p className="text-xl text-slate-600 leading-relaxed font-medium">
          GlobalEPRC provides highly skilled IT professionals who are trained, certified, and ready to support organizations in building strong digital capabilities. Our global IT talent network ensures we deliver experts who meet evolving technology demands.
        </p>
      </div>

      {/* Right side: Staggered Image Grid */}
      <div className="lg:w-1/2 grid grid-cols-2 gap-4 relative">
        <div className="space-y-4">
          <img 
            src="/it.jpeg" 
            alt="Cybersecurity" 
            className="rounded-3xl shadow-xl border border-slate-100 h-64 w-full object-cover" 
          />
          <img 
            src="https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&q=80&w=600" 
            alt="Hardware/Network" 
            className="rounded-3xl shadow-xl border border-slate-100 h-40 w-full object-cover" 
          />
        </div>
        <div className="pt-12">
          <img 
            src="https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&q=80&w=600" 
            alt="Coding/Software" 
            className="rounded-3xl shadow-2xl border border-slate-100 h-[420px] w-full object-cover" 
          />
        </div>
      </div>
    </div>

    {/* Section: Core Roles Grid */}
    <h3 className="text-2xl font-black mb-12 flex items-center gap-3 text-slate-900">
      <span className="h-1 w-12 bg-amber-500 rounded-full"></span>
      Core IT Roles We Provide
    </h3>
    
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
      {/* Role Group: Development */}
      <div className="p-8 rounded-[2.5rem] bg-slate-50 border border-slate-100 hover:bg-white hover:shadow-2xl hover:shadow-amber-900/5 transition-all duration-500 group">
        <div className="flex items-center gap-3 text-slate-900 font-black mb-6">
          <div className="p-3 rounded-2xl bg-white shadow-sm group-hover:bg-gold-gradient group-hover:text-white transition-all">
            <Code size={24} />
          </div>
          <span className="text-lg">Software & Development</span>
        </div>
        <ul className="space-y-4 text-slate-500 font-bold text-sm border-l-2 border-slate-200 ml-6 pl-6">
          <li>• Software Developers</li>
          <li>• Full-Stack Developers</li>
          <li>• Mobile App Developers</li>
          <li>• Web Developers</li>
        </ul>
      </div>

      {/* Role Group: Infrastructure */}
      <div className="p-8 rounded-[2.5rem] bg-slate-50 border border-slate-100 hover:bg-white hover:shadow-2xl hover:shadow-amber-900/5 transition-all duration-500 group">
        <div className="flex items-center gap-3 text-slate-900 font-black mb-6">
          <div className="p-3 rounded-2xl bg-white shadow-sm group-hover:bg-gold-gradient group-hover:text-white transition-all">
            <Database size={24} />
          </div>
          <span className="text-lg">Network Management</span>
        </div>
        <ul className="space-y-4 text-slate-500 font-bold text-sm border-l-2 border-slate-200 ml-6 pl-6">
          <li>• Network Engineers</li>
          <li>• System Administrators</li>
          <li>• IT Support Specialists</li>
        </ul>
      </div>

      {/* Role Group: Data & AI */}
      <div className="p-8 rounded-[2.5rem] bg-slate-50 border border-slate-100 hover:bg-white hover:shadow-2xl hover:shadow-amber-900/5 transition-all duration-500 group">
        <div className="flex items-center gap-3 text-slate-900 font-black mb-6">
          <div className="p-3 rounded-2xl bg-white shadow-sm group-hover:bg-gold-gradient group-hover:text-white transition-all">
            <Microscope size={24} />
          </div>
          <span className="text-lg">Data, AI & Analytics</span>
        </div>
        <ul className="space-y-4 text-slate-500 font-bold text-sm border-l-2 border-slate-200 ml-6 pl-6">
          <li>• Data Scientists</li>
          <li>• Data Analysts</li>
          <li>• AI & ML Specialists</li>
        </ul>
      </div>

      {/* Role Group: Cybersecurity */}
      <div className="p-8 rounded-[2.5rem] bg-slate-50 border border-slate-100 hover:bg-white hover:shadow-2xl hover:shadow-amber-900/5 transition-all duration-500 group">
        <div className="flex items-center gap-3 text-slate-900 font-black mb-6">
          <div className="p-3 rounded-2xl bg-white shadow-sm group-hover:bg-gold-gradient group-hover:text-white transition-all">
            <Lock size={24} />
          </div>
          <span className="text-lg">Cybersecurity & Risk</span>
        </div>
        <ul className="space-y-4 text-slate-500 font-bold text-sm border-l-2 border-slate-200 ml-6 pl-6">
          <li>• Cybersecurity Experts</li>
          <li>• Info Security Analysts</li>
          <li>• SOC Engineers</li>
        </ul>
      </div>

      {/* Role Group: Cloud & Testing */}
      <div className="p-8 rounded-[2.5rem] bg-slate-50 border border-slate-100 hover:bg-white hover:shadow-2xl hover:shadow-amber-900/5 transition-all duration-500 group">
        <div className="flex items-center gap-3 text-slate-900 font-black mb-6">
          <div className="p-3 rounded-2xl bg-white shadow-sm group-hover:bg-gold-gradient group-hover:text-white transition-all">
            <Cloud size={24} />
          </div>
          <span className="text-lg">Cloud & Testing</span>
        </div>
        <ul className="space-y-4 text-slate-500 font-bold text-sm border-l-2 border-slate-200 ml-6 pl-6">
          <li>• IT Project Managers</li>
          <li>• Cloud Specialists (AWS/Azure)</li>
          <li>• Testing Professionals</li>
        </ul>
      </div>
    </div>
  </div>
</section>

      {/* 6. NON-IT RECRUITMENT SOLUTIONS */}
<section className="py-24 bg-white relative overflow-hidden">
  <div className="container mx-auto px-4 md:px-8">
    <div className="flex flex-col lg:flex-row gap-16 items-center mb-16">
      <div className="lg:w-1/2">
        <h2 className="text-4xl md:text-5xl font-black mb-6 text-slate-900 leading-tight">
          Our Recruitment & <br />
          <span className="text-gold-gradient">Talent Solutions (Non-IT)</span>
        </h2>
        <p className="text-xl text-slate-600 leading-relaxed font-medium">
          GlobalEPRC specializes in supplying skilled, semi-skilled, and unskilled manpower across major industries, ensuring reliable, efficient, and job-ready candidates for global employers.
        </p>
      </div>
      
      {/* Visual Highlight: Industrial Workforce */}
      <div className="lg:w-1/2 relative">
        <div className="absolute -inset-4 bg-amber-100/50 rounded-[3rem] -z-10 translate-x-4 translate-y-4"></div>
        <img 
          src="non-it1.jpeg" 
          alt="Technical Workforce" 
          className="rounded-[2.5rem] shadow-2xl w-full h-80 object-cover"
        />
      </div>
    </div>

    <div className="grid md:grid-cols-2 gap-8">
      
      {/* Card 1: Technical & Construction */}
      <div className="space-y-8 p-8 rounded-[3rem] bg-slate-50 border border-slate-100 hover:shadow-xl transition-all duration-500">
        <div className="flex flex-col sm:flex-row gap-6">
          <img 
            src="non-it2.jpeg" 
            className="w-full sm:w-32 h-32 rounded-2xl object-cover shadow-md"
            alt="Technical"
          />
          <div className="flex-1">
            <h3 className="text-xl font-black text-amber-700 mb-4 flex items-center gap-2">
              <Settings size={20}/> Professional & Technical
            </h3>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs font-bold text-slate-600 border-l-2 border-amber-400 pl-4">
              <span>• Engineers</span><span>• Administrators</span><span>• BMS Operators</span>
              <span>• Electrical</span><span>• Mechanical</span><span>• MEP Tech</span>
              <span>• Industrial</span><span>• HVAC Tech</span><span>• Welders</span>
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-xl font-black text-amber-700 mb-4 flex items-center gap-2">
            <Construction size={20}/> Construction & Skilled Trades
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 text-xs font-bold text-slate-600 border-l-2 border-amber-400 pl-4">
            <span>• Carpenters</span><span>• Steel Fixtures</span><span>• Masons</span>
            <span>• Plasters</span><span>• Painters</span><span>• Plumbers</span>
          </div>
        </div>
      </div>

      {/* Card 2: Workforce & Supervisory */}
      <div className="space-y-8 p-8 rounded-[3rem] bg-slate-50 border border-slate-100 hover:shadow-xl transition-all duration-500 flex flex-col justify-between">
        <div>
          <h3 className="text-xl font-black text-amber-700 mb-6 flex items-center gap-2">
            <Truck size={20}/> General Workforce & Support
          </h3>
          <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-xs font-bold text-slate-600 border-l-2 border-amber-400 pl-4">
            <span>• Civil Helpers</span><span>• Skilled Labor</span><span>• Office Boys</span>
            <span>• Cleaners</span><span>• Drivers</span><span>• Security</span>
          </div>
        </div>

        <div className="relative group overflow-hidden rounded-3xl h-40 mb-4">
           <img 
            src="non-it3.jpeg" 
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
            alt="Workforce"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent flex items-end p-6">
            <p className="text-white font-bold text-sm">Reliable & Job-Ready Workforce</p>
          </div>
        </div>

        <div className="bg-amber-100/50 p-6 rounded-[2rem] border border-amber-200">
          <h3 className="text-lg font-black text-amber-800 mb-4 flex items-center gap-2">
            <Anchor size={20}/> Specialized Roles
          </h3>
          <div className="grid grid-cols-2 gap-4 text-xs font-black text-amber-900">
            <span className="flex items-center gap-2"><CheckCircle size={14} className="text-amber-600"/> Team Leaders</span>
            <span className="flex items-center gap-2"><Anchor size={14} className="text-amber-600"/> Mooring Crew</span>
            <span className="flex items-center gap-2"><CheckCircle size={14} className="text-amber-600"/> Housekeeping</span>
            <span className="flex items-center gap-2"><Flame size={14} className="text-amber-600"/> Fire Fighters</span>
          </div>
        </div>
      </div>

    </div>
  </div>
</section>

      {/* 7. FINAL CALL TO ACTION */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8">
          <div className="bg-slate-950 rounded-[3rem] p-12 text-center text-white relative overflow-hidden shadow-2xl">
            <div className="absolute inset-0 bg-gold-gradient opacity-10 blur-3xl"></div>
            <h3 className="text-3xl font-bold mb-6 relative z-10">Experience a smooth and reliable recruitment journey.</h3>
            <p className="text-slate-400 mb-10 max-w-2xl mx-auto relative z-10">
              We adhere to international standards, transparent processes, and strict compliance to ensure both employers and job seekers benefit.
            </p>
            <button className="bg-gold-gradient text-white px-10 py-4 rounded-2xl font-bold text-lg shadow-xl hover:scale-105 transition-transform">
              Partner With Us
            </button>
          </div>
        </div>
      </section>

    </div>
  );
};

export default ManpowerRecruitmentPage;